import { auth } from './config';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  updateProfile,
  signOut as fbSignOut,
  RecaptchaVerifier,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  sendEmailVerification as fbSendEmailVerification,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  getMultiFactorResolver,
} from 'firebase/auth';

/* ---------- OAuth providers ---------- */
export const googleProvider = new GoogleAuthProvider();

export const oauthProviders = {
  google: googleProvider,
};

/* ---------- Email / password ---------- */
export async function signUpWithEmail(name, email, password) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  if (name) {
    await updateProfile(cred.user, { displayName: name });
  }
  return cred.user;
}

export async function signInWithEmail(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  return cred.user;
}

/* ---------- Redirect sign-in progress marker ---------- */
// signInWithRedirect navigates the whole page away to the provider and back.
// Firebase gives no synchronous "a redirect is in progress" signal on return,
// so we drop our own marker before leaving. On the way back the sign-in UI
// reads it on mount to show a loading indicator immediately, instead of
// flashing the login form for the moment it takes getRedirectResult / the auth
// listener to resolve. sessionStorage (not local) so it's scoped to this tab
// and can't get stuck across sessions.
const REDIRECT_PENDING_KEY = 'authRedirectPending';

export function isRedirectPending() {
  try {
    return sessionStorage.getItem(REDIRECT_PENDING_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function markRedirectPending() {
  try {
    sessionStorage.setItem(REDIRECT_PENDING_KEY, '1');
  } catch (_) {
    /* storage disabled — falls back to the pre-existing brief flash */
  }
}

export function clearRedirectPending() {
  try {
    sessionStorage.removeItem(REDIRECT_PENDING_KEY);
  } catch (_) {
    /* no-op */
  }
}

/* ---------- Social sign-in ---------- */
// Local dev uses a popup (easy to test); the deployed app uses a full-page
// redirect. With redirect the page navigates away and auth state resumes on
// return (see getRedirectResult handling in config.js).
export async function signInWithProvider(providerKey) {
  const provider = oauthProviders[providerKey];
  if (!provider) throw new Error(`Unknown provider: ${providerKey}`);
  if (process.env.NODE_ENV === 'development') {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  }
  // Mark before we navigate away so the return trip can show a loading state.
  markRedirectPending();
  await signInWithRedirect(auth, provider);
  return null;
}

/* ---------- Email link (passwordless) sign-in ---------- */
// Replaces phone-as-primary-factor sign-in: it's equally passwordless, but it
// yields an email address, which is what the rest of the system runs on — the
// approval/welcome mail and the members Google Group are both keyed on it, and
// a Google Group can't hold a phone number. (Phone lives on only as the SMS
// second factor below.)
//
// Requires "Email link (passwordless sign-in)" to be enabled on the
// Email/Password provider in the Firebase console, and the sending domain to be
// listed under Authentication -> Settings -> Authorized domains.
const EMAIL_FOR_LINK_KEY = 'emailForSignIn';

// Where the emailed link returns to. Defaults to the current page minus its
// hash (Firebase appends its own query params, and a hash would trail them),
// so a visitor who started at /login?next=/events lands back on that same URL.
function defaultLinkReturnUrl() {
  const { origin, pathname, search } = window.location;
  return `${origin}${pathname}${search}`;
}

export async function sendSignInLink(email, returnUrl = defaultLinkReturnUrl()) {
  // handleCodeInApp must be true for email-link sign-in.
  await sendSignInLinkToEmail(auth, email, { url: returnUrl, handleCodeInApp: true });
  // Remember the address so the returning link completes without re-asking —
  // the same-device case, which is most of them.
  try {
    localStorage.setItem(EMAIL_FOR_LINK_KEY, email);
  } catch (_) {
    /* storage disabled — the caller will prompt for the address instead */
  }
}

// True when the current URL is a sign-in link Firebase issued.
export function isEmailLink(href = window.location.href) {
  return isSignInWithEmailLink(auth, href);
}

// The address the link was requested for, or '' when the link was opened on a
// different device/browser than it was requested from.
export function storedLinkEmail() {
  try {
    return localStorage.getItem(EMAIL_FOR_LINK_KEY) || '';
  } catch (_) {
    return '';
  }
}

export async function completeEmailLinkSignIn(email, href = window.location.href) {
  const cred = await signInWithEmailLink(auth, email, href);
  try {
    localStorage.removeItem(EMAIL_FOR_LINK_KEY);
  } catch (_) {
    /* no-op */
  }
  return cred.user;
}

/* ---------- reCAPTCHA (used by the MFA flows) ---------- */
// Verifiers are keyed by container id so the MFA enrollment and MFA sign-in
// flows can each own an independent invisible reCAPTCHA.
const _verifiers = {};

export function getRecaptchaVerifier(containerId = 'recaptcha-container') {
  if (!_verifiers[containerId]) {
    // grecaptcha marks the exact DOM node it renders into as "already rendered",
    // and for an invisible widget the badge is appended to <body>, not inside
    // the container. So clearing the container's innerHTML isn't enough — the
    // node stays poisoned and the next render throws "reCAPTCHA has already been
    // rendered in this element". Render into a fresh child node each time so
    // grecaptcha always gets a virgin element.
    const host = document.getElementById(containerId);
    let target = containerId;
    if (host) {
      host.innerHTML = '';
      target = document.createElement('div');
      host.appendChild(target);
    }
    _verifiers[containerId] = new RecaptchaVerifier(auth, target, {
      size: 'invisible',
    });
  }
  return _verifiers[containerId];
}

// Resets the verifier so a failed attempt can be retried cleanly.
export function clearRecaptcha(containerId = 'recaptcha-container') {
  const v = _verifiers[containerId];
  if (v) {
    try {
      v.clear();
    } catch (_) {
      /* no-op */
    }
    delete _verifiers[containerId];
  }
}

/* ---------- Email verification ---------- */
export function sendVerificationEmail(user) {
  return fbSendEmailVerification(user);
}

/* ---------- SMS multi-factor: enrollment ---------- */
// A signed-in user with a verified email adds an SMS second factor.
// Step 1: send the code. Returns a verificationId.
export async function startMfaEnrollment(user, phoneNumber, containerId) {
  clearRecaptcha(containerId);
  const verifier = getRecaptchaVerifier(containerId);
  const session = await multiFactor(user).getSession();
  const provider = new PhoneAuthProvider(auth);
  return provider.verifyPhoneNumber({ phoneNumber, session }, verifier);
}

// Step 2: confirm the code and enroll the factor.
export async function finishMfaEnrollment(user, verificationId, code, displayName = 'My phone') {
  const cred = PhoneAuthProvider.credential(verificationId, code);
  const assertion = PhoneMultiFactorGenerator.assertion(cred);
  await multiFactor(user).enroll(assertion, displayName);
}

/* ---------- SMS multi-factor: sign-in resolution ---------- */
// Called when sign-in throws auth/multi-factor-auth-required.
export function getMfaResolver(error) {
  return getMultiFactorResolver(auth, error);
}

// Step 1: send the code to the enrolled phone hint. Returns a verificationId.
export async function startMfaSignIn(resolver, hintIndex, containerId) {
  clearRecaptcha(containerId);
  const verifier = getRecaptchaVerifier(containerId);
  const hint = resolver.hints[hintIndex];
  const provider = new PhoneAuthProvider(auth);
  return provider.verifyPhoneNumber(
    { multiFactorHint: hint, session: resolver.session },
    verifier
  );
}

// Step 2: confirm the code and complete sign-in.
export async function finishMfaSignIn(resolver, verificationId, code) {
  const cred = PhoneAuthProvider.credential(verificationId, code);
  const assertion = PhoneMultiFactorGenerator.assertion(cred);
  return resolver.resolveSignIn(assertion);
}

// True when a factor id is the SMS/phone factor.
export function isPhoneFactor(factorId) {
  return factorId === PhoneMultiFactorGenerator.FACTOR_ID;
}

/* ---------- Sign out ---------- */
export function signOut() {
  return fbSignOut(auth);
}

/* ---------- Friendly error messages ---------- */
export function friendlyAuthError(err) {
  const code = err && err.code ? err.code : '';
  switch (code) {
    case 'auth/operation-not-allowed':
      return 'This sign-in method isn’t enabled yet. (Enable it in the Firebase console.)';
    case 'auth/email-already-in-use':
      return 'That email already has an account — try signing in instead.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/weak-password':
    case 'auth/password-does-not-meet-requirements':
      return 'Password must be at least 8 characters and include an uppercase letter, a lowercase letter, and a number.';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password.';
    case 'auth/user-not-found':
      return 'No account found with that email.';
    // Sign-in links are single-use and time-limited.
    case 'auth/invalid-action-code':
    case 'auth/expired-action-code':
      return 'That sign-in link has expired or was already used. Please request a new one.';
    case 'auth/invalid-email-link':
      return 'That sign-in link doesn’t look valid. Please request a new one.';
    case 'auth/invalid-phone-number':
      return 'Please enter a valid phone number, including country code.';
    case 'auth/invalid-verification-code':
      return 'That verification code is incorrect. Please try again.';
    case 'auth/invalid-app-credential':
      return 'Couldn’t verify the reCAPTCHA. Please try sending the code again.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a bit and try again.';
    case 'auth/popup-closed-by-user':
      return 'The sign-in window was closed before finishing.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in popup. Please allow popups and retry.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email using a different sign-in method.';
    case 'auth/requires-recent-login':
      return 'For your security, please sign in again before changing two-step settings.';
    case 'auth/unverified-email':
      return 'Please verify your email before enabling two-step verification.';
    case 'auth/second-factor-already-in-use':
      return 'That phone number is already set up for two-step verification.';
    case 'auth/maximum-second-factor-count-exceeded':
      return 'You’ve reached the maximum number of second factors.';
    case 'auth/unsupported-first-factor':
      return 'Two-step verification can’t be added to a phone-only account.';
    default:
      return (err && err.message) || 'Something went wrong. Please try again.';
  }
}
